// The LCARS frame's content area: the PAGE SLOT (left column — the routed page of the current
// URL, built by the canvas renderer in client.js from views/<page>.js) and the OVERLAYS (right
// column — Messages / Notifications, part of the main layout; their items NAVIGATE: a message
// opens /messages/<id>, a notification its action link). User 2026-10-10: pages are real routes
// with their own controllers, only messages and notifications are overlays of main.
// Everything lives in the frame's content area (main.content), over the map. ONE layout for
// desktop and VR (sizes as in imperial-lcars examples/user1.html); VR only lays it out for a
// smaller viewport.
import { setDefaults, box, label, button, input, lcars } from 'imperial-lib';
import { MESSAGES, NOTIFICATIONS } from '../hybrid/dummy-data.js';

export const NOTIFICATION_COUNT = NOTIFICATIONS.length;

const NONE = [0, 0, 0, 0];
const colorAttention = [0.5, 0, 0];
// which overlay is open survives a rebuild of the frame (the VR relayout disposes + recreates
// everything, e.g. right after a Settings → XR change that alters the layout)
let remembered = { rightKind: null };
// LOGIN is an overlay of main, top right (user 2026-10-11: "not a separate controller, belongs to
// main and on the right"): e-mail → SEND CODE → 6-digit code → LOGIN (providers/login.js RPC).
let loginState = emptyLoginState();

const MUTED = [0.7, 0.75, 0.9];

function emptyLoginState() {
	return { step: 'email', email: '', code: '', error: '', mail: false, busy: false };
}

// is an overlay (messages / notifications / login) open? (hud-lcars.js decides the phone side menu)
export function overlayOpen() {
	return !!remembered.rightKind;
}

// the text currently typed into an imperial-lib input box
function fieldText(field) {
	return (field.opts?.text ?? field._text?.opts?.text ?? '').trim();
}

// main: the LcarsBox frame; font: loaded text-base font;
// applyOrder(root): the HUD's draw-order regime for a freshly built subtree (hud-lcars.js)
export function createHudPanels(main, { font, applyOrder, topInset = 0 }) {
	setDefaults({ font }); // the overlay rows are imperial-lib boxes with text: they need the default font
	const ca = main.contentArea;
	const pad = 16;
	// phone: ONE column — the page and the overlays take the whole content width and an opened
	// overlay replaces the page (user 2026-10-12: "no two columns")
	const phone = !!globalThis.game?.phoneLayout;
	const colW = phone ? ca.w - 2 * pad : Math.min(620, Math.round(ca.w * 0.5));
	const listH = ca.h - 2 * pad;
	const rightW = phone ? colW : Math.min(520, ca.w - colW - 3 * pad);
	// phone = the TRICORDER (user 2026-10-21): no bands — the menu is the frame's left column (the spine, tapped, builds the
	// desktop column, hud-lcars.js); the page slot is the content area beside it.
	// topInset (phone, tooltip pill shown): the page slot starts below the pill so it never covers the page header (#33)
	const slotH = listH - topInset;

	let page = null;
	let rightPanel = null;
	let rightPanelWrap = null;
	let spacer = null;
	let { rightKind } = remembered;
	let disposed = false; // set by dispose(): async replies of a login step started on this instance redraw through game.hudPanels
	let loginFieldStep = null;
	let loginField = null; // the login step's input: its typed text is carried across a rebuild (phone relayout after the pill)

	function remember() {
		remembered = { rightKind };
	}

	function clear(node) {
		if (!node) {
			return;
		}
		// An LcarsBox does not dispose the boxes inside its content: their click/text targets stayed registered
		// in the input core after the panel was replaced (the e-mail step's SEND CODE button kept winning the tie
		// against the code step's LOGIN button — ticket 31). Unregister every target in the subtree first.
		node.traverse?.((o) => {
			try {
				o.clickable?.dispose?.();
				o._textHandle?.dispose?.();
			} catch (e) {
				console.warn('[hud-panels] target dispose failed', e);
			}
		});
		node.dispose?.();
		node.parent?.remove(node);
	}

	// flex only places Box children: an LcarsBox goes into a transparent Box
	function wrapLcars(panel) {
		const wrapper = box({ backgroundColor: NONE });
		wrapper.add(panel);
		wrapper.layout();
		return wrapper;
	}

	// content = ONE row Box [page | spacer, right column]; phone: ONE column [page | overlay]
	const root = box({
		width: ca.w - 2 * pad,
		height: listH - topInset,
		direction: phone ? 'column' : 'row',
		gap: pad,
		justifyContent: phone ? 'flex-start' : 'space-between',
		backgroundColor: NONE,
	});
	const rightCol = box({ width: rightW, height: slotH, direction: 'column', gap: pad, backgroundColor: NONE });
	root.position.set(0, -topInset / 2, 0.1); // the top edge moves down by topInset, the bottom stays
	main.content.add(root);

	function relayout() {
		clear(spacer);
		spacer = null;
		rightCol.parent?.remove(rightCol); // re-added last: children are laid out in add order
		const overlayReplacesPage = phone && !!rightPanelWrap;
		if (page && !overlayReplacesPage) {
			page.visible = true;
			root.add(page);
		} else {
			if (page) { // phone with an overlay open: the page waits, invisible, outside the layout
				page.parent?.remove(page);
				page.visible = false;
			}
			if (!overlayReplacesPage) {
				spacer = box({ width: colW, height: slotH, backgroundColor: NONE });
				root.add(spacer);
			}
		}
		if (rightPanelWrap) {
			rightCol.add(rightPanelWrap);
		}
		if (!phone || rightPanelWrap) { // the phone column has no room for an empty right column
			root.add(rightCol);
		}
		rightCol.layout();
		root.layout();
		applyOrder(root);
	}

	function buildLogin() {
		const w = rightW - 40;
		const rows = loginState.step === 'email' ? loginEmailRows(w) : loginCodeRows(w);
		return box({
			width: rightW - 8,
			height: slotH - 34,
			direction: 'column',
			gap: 12,
			padding: [16, 16, 16, 16],
			alignItems: 'flex-start',
			backgroundColor: NONE,
		}, rows);
	}

	function loginErrorRows(w) {
		if (!loginState.error) {
			return [];
		}
		return [label({
			text: loginState.error,
			fontSize: 13,
			color: [1, 0.35, 0.3],
			width: w,
			textWrap: true,
			maxWidth: w,
		})];
	}

	function showLoginError(st, error) {
		st.error = error;
		st.busy = false;
		redrawRight();
	}

	// a reply (SEND CODE / LOGIN) may arrive after a relayout replaced these panels: the live instance redraws
	function redrawRight() {
		if (disposed) {
			globalThis.game?.hudPanels?.rerenderRight?.();
		} else {
			rebuildRight();
		}
	}
	function saveLoginField() {
		if (rightKind !== 'login' || !loginField) return;
		const t = fieldText(loginField);
		if (loginFieldStep === 'email') loginState.email = t; else loginState.code = t; // the step the field was built for, not the current one
	}

	// step 1: e-mail → SEND CODE
	function loginEmailRows(w) {
		const st = loginState;
		// textAlign left: the text engine centres a field's text by default (imperial-text-base
		// box-text `align ?? 'center'`), which is right for the frame's X/Y and the code field but
		// wrong for an address (user 2026-10-11: "the centered email in the input field")
		const field = input({ width: Math.min(360, w), height: 44, fontSize: 18, text: st.email || '', textAlign: 'left' });
		loginField = field; loginFieldStep = st.step;
		async function send() {
			if (st.busy) {
				return;
			}
			st.busy = true;
			st.email = fieldText(field);
			const r = await provider.request('game', 'login.js', 'sendCode', { email: st.email });
			if (!r || r.$offline) {
				return showLoginError(st, 'NOT CONNECTED - TRY AGAIN');
			}
			if (r.error) {
				return showLoginError(st, r.error);
			}
			Object.assign(st, { step: 'code', email: r.email, code: '', mail: r.mail, error: '', busy: false });
			redrawRight();
		}
		field.addEventListener?.('submit', send);
		const intro = 'ENTER THE E-MAIL ADDRESS OF YOUR ACCOUNT. A LOGIN CODE WILL BE SENT TO IT.';
		return [
			label({ text: intro, fontSize: 14, color: MUTED, width: w, textWrap: true, maxWidth: w }),
			label({ text: 'E-MAIL', fontSize: 12, color: MUTED }),
			field,
			button({ text: 'SEND CODE', width: 160, height: 38, fontSize: 15, onClick: send }),
			...loginErrorRows(w),
		];
	}

	// step 2: the 6-digit code → LOGIN (or back to another e-mail)
	function loginCodeRows(w) {
		const st = loginState;
		const field = input({ width: Math.min(260, w), height: 44, fontSize: 22, text: st.code || '', textAlign: 'center' });
		loginField = field; loginFieldStep = st.step;
		async function login() {
			if (st.busy) {
				return;
			}
			st.busy = true;
			const code = fieldText(field);
			const r = await provider.request('game', 'login.js', 'login', { code });
			if (!r || r.$offline) {
				return showLoginError(st, 'NOT CONNECTED - TRY AGAIN');
			}
			if (r.error) {
				return showLoginError(st, r.error);
			}
			loginState = emptyLoginState();
			rightKind = null;
			remembered = { rightKind: null };
			redrawRight(); // closes the overlay: rebuildHud below is a partial update that keeps these panels (ticket 31: the code panel stayed open)
			await game.rebuildHud?.(); // Login → Messages / Notifications
		}
		function otherEmail() {
			Object.assign(st, { step: 'email', code: '', error: '', busy: false });
			redrawRight();
		}
		field.addEventListener?.('submit', login);
		let where = 'LOGGED ON THE SERVER (NO MAIL CONFIGURED)';
		if (st.mail) {
			where = 'MAILED TO ' + String(st.email).toUpperCase();
		}
		const intro = `A 6-DIGIT CODE WAS ${where}. ENTER IT HERE.`;
		return [
			label({ text: intro, fontSize: 14, color: MUTED, width: w, textWrap: true, maxWidth: w }),
			label({ text: 'CODE', fontSize: 12, color: MUTED }),
			field,
			box({ direction: 'row', gap: 10, backgroundColor: NONE }, [
				button({ text: 'LOGIN', width: 140, height: 38, fontSize: 15, onClick: login }),
				button({ text: 'OTHER E-MAIL', width: 140, height: 38, fontSize: 13, onClick: otherEmail }),
			]),
			...loginErrorRows(w),
		];
	}

	// Messages / Notifications: one row per entry, a click navigates to its link
	function overlayList() {
		let entries;
		if (rightKind === 'messages') {
			entries = MESSAGES.map((msg) => ({
				text: `${msg.subject}  -  ${msg.from}`,
				hot: false,
				link: `/messages/${msg.id}`,
			}));
		} else {
			entries = NOTIFICATIONS.map((n, i) => ({ text: n.text, hot: i < 2, link: n.link }));
		}
		return box({
			width: rightW - 8,
			height: slotH - 34,
			direction: 'column',
			gap: 8,
			padding: [8, 8, 8, 8],
			overflow: 'scroll',
			scrollable: true,
			alignItems: 'flex-start',
			backgroundColor: NONE,
		}, entries.map((e) => box({
			width: rightW - 40,
			height: 44,
			text: e.text,
			fontSize: 13,
			color: [0.9, 0.9, 1],
			align: 'left',
			padding: [8, 10, 8, 10],
			backgroundColor: e.hot ? [0.4, 0.06, 0.06, 1] : [0.09, 0.1, 0.16, 1],
			borderRadii: [6, 6, 6, 6],
			clickable: true,
			onClick: () => navigation.loadPage(e.link),
		})));
	}

	// the right column's LCARS panel: title top left, X closes it
	function overlayPanel(title, content) {
		function closeOverlay() {
			rightKind = null;
			rebuildRight();
		}
		return lcars({
			width: rightW,
			height: slotH,
			font,
			fontSize: 14,
			color: [1, 1, 1, .5],
			backgroundColor: [0.03, 0.04, 0.09, 1],
			content: { backgroundColor: NONE },
			borders: {
				top: {
					left: [{ label: title, fontSize: 14 }],
					right: [{ button: 'X', background: colorAttention, color: [1, 1, 1], onClick: closeOverlay }],
				},
			},
		}, [content]);
	}

	function rebuildRight() {
		saveLoginField();
		loginField = null;
		remember();
		clear(rightPanel);
		rightPanel = null;
		clear(rightPanelWrap);
		rightPanelWrap = null;
		if (rightKind === 'login') {
			rightPanel = overlayPanel('LOGIN', buildLogin());
		} else if (rightKind) {
			rightPanel = overlayPanel(rightKind.toUpperCase(), overlayList());
		}
		if (rightPanel) {
			rightPanelWrap = wrapLcars(rightPanel);
			rightPanelWrap.userData.rayOpaque = true; // a ray ends on an opened overlay, like on a page
		}
		relayout();
	}

	// The routed page (a box-ui tree the renderer built) goes into the left column. The
	// renderer owns its lifetime (removeScene disposes it): here it is only (un)parented.
	function setPage(container) {
		let fitted = false;
		if (page && page !== container) {
			page.parent?.remove(page);
			// a container the renderer no longer owns (a newer render replaced it) dies here: unparented
			// but undisposed, its click targets stayed registered in the input core as invisible walls
			// at page-pixel coordinates in WORLD space, near the home system (review 2026-10-16)
			if (globalThis.$renderer?._levels?.[1] !== page) {
				page.dispose?.();
			}
		}
		page = container || null;
		if (page) {
			// a controller ray ends anywhere on an opened page, gaps included (web-xr guiSurfaceHit)
			page.userData.rayOpaque = true;
			fitted = fitPage(page);
		}
		relayout();
		return fitted;
	}

	// A page whose view was evaluated before this frame existed carries the server's default size
	// (ui/views pageSize without a frame: 600 × 700) — the renderer builds it asynchronously (fonts),
	// so it can mount AFTER adopt() looked for it. Re-render it once for this frame's slot (user
	// 2026-10-16: "the panel is unpredictable, either too big or too small").
	function fitPage(container) {
		const l = container.__lcars;
		const fitKey = `${colW}x${slotH}`;
		if (!l || (container.__fittedTo === main && container.__fittedKey === fitKey)) {
			return false;
		}
		container.__fittedTo = main;
		container.__fittedKey = fitKey;
		const want = { w: colW, h: slotH };
		if (l.width === want.w && l.height === want.h) {
			return false;
		}
		globalThis.$renderer?._levelState?.[1]?.controller?.$rerender?.();
		return true; // one render is on its way; the caller must not start a second one
	}

	relayout();
	if (rightKind) {
		rebuildRight();
	}
	return {
		// the size of the page slot (ui/views/index.js pageSize renders the routed pages to it):
		// desktop the left column, phone the whole content column
		pageSlot() {
			return { w: colW, h: slotH };
		},
		// adopt the page the renderer built before this frame existed (cold load of /settings, or
		// the VR relayout rebuilding the frame) and re-render it for this frame's sizes. Called by
		// hud-lcars.js AFTER game.hudPanels points here (the re-render mounts through game.hudPanels).
		adopt() {
			const pending = globalThis.$renderer?._levels?.[1];
			if (!pending) {
				return;
			}
			if (!setPage(pending)) { // fitPage already re-renders a page of the wrong size; two concurrent
				globalThis.$renderer._levelState?.[1]?.controller?.$rerender?.(); // renders of one container leaked the first result
			}
		},
		// Messages / Notifications buttons: toggle the right overlay of that kind
		showPanel(kind) {
			rightKind = rightKind === kind ? null : kind;
			rebuildRight();
		},
		setPage,
		rerenderRight() { // after another instance was replaced mid-flight: take the shared state
			({ rightKind } = remembered);
			rebuildRight();
		},
		page: () => page,
		state() {
			return { page: page ? page.__uiRootView?.key ?? 'page' : null, rightKind, path: location.pathname };
		},
		// the page is NOT disposed with the frame: it belongs to the renderer and gets adopted by the next frame
		dispose() {
			saveLoginField(); // the typed e-mail / code survives the rebuild (loginState is module-level)
			disposed = true;
			if (page) {
				page.parent?.remove(page);
			}
			page = null;
			clear(root);
		},
	};
}
